import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { SfmcMobilePush } from 'capacitor-sfmc-mobile-push';
import { useAuthStore } from '@/lib/core/stores/useAuthStore';
import { randomUuid } from '@/lib/core/utils';
import { registerPushNotificationsIfConsented } from '@/lib/notifications/pushNotifications';

/**
 * TEMPORARY — cross-environment QA override, remove once test/prod environments are aligned.
 *
 * Login happens against a test Entra tenant, whose `Customer.PublicGuid` values mean nothing
 * to the production Marketing Cloud org this app currently points at (the client is
 * deliberately testing test-environment login against prod Marketing Cloud simultaneously).
 * For known QA users, use their real production Salesforce Contact ID as the SFMC contact
 * key instead, so registration lands on the actual existing production contact rather than
 * creating an orphaned one keyed by a test-environment GUID Marketing Cloud Connect (if
 * enabled) wouldn't recognize.
 *
 * Keyed by email (`UserDto.email`) since that's stable across both environments for these
 * specific accounts. Values are Salesforce `PersonContactId` (Contact ID, "003..."), not the
 * PersonAccount `Id` ("001...") — Marketing Cloud Connect links to Contact records.
 */
const PROD_CONTACT_KEY_OVERRIDES: Record<string, string> = {
  'amorim.lux.activemember@shareit.network': '003Sb00000p6ujlIAA',
  'amorim.lux.multicard@shareit.network': '003Sb00000p7Db3IAE',
};

/** Flip to `false` (or unset the env var) once test/prod environments match — see comment above. */
const USE_PROD_CONTACT_KEY_OVERRIDE = import.meta.env.VITE_SFMC_USE_PROD_CONTACT_KEY_OVERRIDE === 'true';

const ANONYMOUS_CONTACT_KEY_STORAGE_KEY = 'sfmc_anonymous_contact_key';

/**
 * Stable per-install fallback for users with no `contactId` yet (pre-login, or backend
 * Salesforce/MuleSoft linking hasn't completed — see `ResolveContactIdAsync`). Must be
 * persisted rather than generated fresh each call: a plain `randomUuid()` inline in the effect
 * below would mint a new, unfindable anonymous identity on every app launch, scattering one
 * physical device's registration across many orphaned SFMC contacts instead of one.
 */
function getOrCreateAnonymousContactKey(): string {
  const existing = localStorage.getItem(ANONYMOUS_CONTACT_KEY_STORAGE_KEY);
  if (existing) return existing;

  const generated = randomUuid();
  localStorage.setItem(ANONYMOUS_CONTACT_KEY_STORAGE_KEY, generated);
  return generated;
}

export interface ResolvedContactKey {
  contactKey: string;
  /** True when PROD_CONTACT_KEY_OVERRIDES supplied the key instead of the real/anonymous one. */
  usedOverride: boolean;
}

/**
 * Pure resolution of "what contact key should be set right now" — extracted so the diagnostics
 * screen (DebugSfmc.tsx) can show the same effective value this hook actually sends natively,
 * rather than the raw (dev-environment) contactId alone, which is misleading on its own since
 * `PROD_CONTACT_KEY_OVERRIDES` may substitute it entirely for known QA accounts.
 */
export function resolveContactKey(
  contactId: string | null | undefined,
  email: string | null | undefined
): ResolvedContactKey {
  const override = USE_PROD_CONTACT_KEY_OVERRIDE && email ? PROD_CONTACT_KEY_OVERRIDES[email] : undefined;
  return {
    contactKey: override ?? contactId ?? getOrCreateAnonymousContactKey(),
    usedOverride: Boolean(override),
  };
}

/**
 * Keeps the Marketing Cloud contact key (`profileId`) in sync with the authenticated user,
 * so backend-triggered sends and Marketing-Cloud-side segmentation can target the right
 * contact. Single choke point for this instead of scattering setContactKey() calls across
 * every `setUser`/`clearUser` call site in the auth service (login, magic-link, token refresh,
 * logout) — see plan A9.
 *
 * `UserDto.customerPublicGuid` (backend's `Customer.PublicGuid`) is the shared identity
 * convention with the backend's Marketing Cloud integration (Part B) — both sides must agree
 * on this value. See `PROD_CONTACT_KEY_OVERRIDES` above for the temporary cross-environment
 * QA exception to this.
 *
 * Per Salesforce's docs the contact key can be reassigned but never cleared, so on logout a
 * fresh random id is set rather than attempting to unset it.
 */
export function useSfmcContactKeySync() {
  const contactId = useAuthStore(state => state.user?.contactId);
  const email = useAuthStore(state => state.user?.email);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const { contactKey, usedOverride } = resolveContactKey(contactId, email);

    if (usedOverride) {
      console.warn('[SfmcMobilePush] Using temporary prod contact-key override for', email);
    }

    SfmcMobilePush.setContactKey({ contactKey })
      // Re-assert push registration against whichever identity is now current — without this,
      // a device that already opted in under a previous contact key (or the anonymous one set
      // on logout) never re-enables push after the contact key changes again, since nothing
      // else in the login/logout flow calls enablePush(). No-op when the user never consented.
      .then(() => registerPushNotificationsIfConsented())
      .catch(error => {
        console.warn('[SfmcMobilePush] setContactKey() failed:', error);
      });
  }, [contactId, email]);
}
